import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Search, CheckCircle2, Loader2 } from "lucide-react";
import api from "@/lib/api";

export default function SalesList() {
  const [sales, setSales] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [completing, setCompleting] = useState(null);

  const fetchSales = async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      const { data } = await api.get("/sales", { params });
      setSales(data);
    } catch { /* */ }
    setLoading(false);
  };

  useEffect(() => { fetchSales(); }, []);
  const handleSearch = (e) => { e.preventDefault(); fetchSales(); };

  const completeSale = async (saleId) => {
    if (!window.confirm("Concluir esta venda? Isso irá criar assinaturas no Stripe.")) return;
    setCompleting(saleId);
    try {
      const { data } = await api.put(`/sales/${saleId}/complete`);
      if (data.stripe_errors?.length > 0) {
        alert(`Venda concluída com avisos Stripe:\n${data.stripe_errors.join("\n")}`);
      }
      fetchSales();
    } catch (e) {
      alert(e.response?.data?.detail || "Erro ao concluir venda");
    }
    setCompleting(null);
  };

  const statusConfig = {
    completed: { badge: "crm-badge crm-badge-green", label: "Concluída" },
    pending: { badge: "crm-badge crm-badge-orange", label: "Pendente" },
  };

  return (
    <div data-testid="sales-list-page">
      <div className="flex items-center justify-between mb-8">
        <h1 className="crm-heading">Vendas</h1>
        <div className="flex gap-3">
          <Link to="/crm/sales/wizard" data-testid="wizard-btn" className="crm-btn-primary"><Plus className="w-4 h-4" /> Assistente de Venda</Link>
          <Link to="/crm/sales/new" data-testid="add-sale-btn" className="crm-btn-secondary"><Plus className="w-4 h-4" /> Venda Rápida</Link>
        </div>
      </div>

      <div className="flex gap-3 mb-6">
        <form onSubmit={handleSearch} className="flex-1 relative">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: "var(--crm-text-dim)" }} />
          <input value={search} onChange={(e) => setSearch(e.target.value)} data-testid="sales-search" placeholder="Buscar por cliente..." className="crm-input pl-10" />
        </form>
      </div>

      <div className="crm-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="crm-table">
            <thead><tr><th>Data</th><th>Cliente</th><th>Itens</th><th>Total</th><th>Mensal</th><th>Status</th><th className="text-right">Ações</th></tr></thead>
            <tbody>
              {loading ? (
                [...Array(3)].map((_, i) => <tr key={i}><td colSpan={7}><div className="h-5 rounded animate-pulse" style={{ background: "var(--crm-border)" }} /></td></tr>)
              ) : sales.length === 0 ? (
                <tr><td colSpan={7} className="text-center py-12" style={{ color: "var(--crm-text-dim)" }}>Nenhuma venda encontrada</td></tr>
              ) : (
                sales.map((s) => {
                  const sc = statusConfig[s.status] || { badge: "crm-badge crm-badge-gray", label: s.status };
                  return (
                    <tr key={s.id} data-testid={`sale-row-${s.id}`}>
                      <td>{new Date(s.created_at).toLocaleDateString("pt-BR")}</td>
                      <td className="primary">{s.customer_name}</td>
                      <td>{s.items.length} item(s)</td>
                      <td style={{ color: "var(--crm-text)", fontWeight: 600, fontSize: "15px" }}>¥{s.total.toLocaleString()}</td>
                      <td>{s.monthly_total > 0 ? <span style={{ color: "var(--crm-accent)" }}>¥{s.monthly_total.toLocaleString()}/mês</span> : "—"}</td>
                      <td><span className={sc.badge}>{sc.label}</span></td>
                      <td className="text-right">
                        {s.status === "pending" && (
                          <button onClick={() => completeSale(s.id)} disabled={completing === s.id}
                            className="inline-flex items-center gap-1.5 text-sm font-bold px-4 py-2 rounded-xl transition-all"
                            style={{ color: "white", background: "#10b981", boxShadow: "0 2px 8px rgba(16,185,129,0.25)" }}
                            data-testid={`complete-sale-${s.id}`}>
                            {completing === s.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                            Concluir
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
      <p className="text-xs mt-3" style={{ color: "var(--crm-text-dim)" }}>{sales.length} venda(s)</p>
    </div>
  );
}
